import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Copy, Plus, X, Calendar, Eye, Clock } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/stores/toast';
import { formatDate } from '@/lib/utils';

interface ShareLink {
  id: string;
  tier: 'STARTER' | 'FULL';
  label: string | null;
  expiresAt: string;
  maxAccess: number;
  accessCount: number;
  lastAccessAt: string | null;
  lastAccessIp: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export function PackShareLinksPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [tier, setTier] = useState<'STARTER' | 'FULL'>('FULL');
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [maxAccess, setMaxAccess] = useState(50);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['compliance-pack', 'shares'],
    queryFn: async () => (await api.get<{ items: ShareLink[] }>('/api/compliance-pack/shares')).data,
  });

  const create = useMutation({
    mutationFn: async () =>
      (await api.post<{ url: string; id: string; token: string }>('/api/compliance-pack/shares', { tier, label: label || undefined, expiresInDays, maxAccess })).data,
    onSuccess: (res) => {
      setCreatedUrl(res.url);
      qc.invalidateQueries({ queryKey: ['compliance-pack', 'shares'] });
    },
    onError: (err) => toast.error('Could not create share link', extractApiError(err).message),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => api.post(`/api/compliance-pack/shares/${id}/revoke`),
    onSuccess: () => {
      toast.success('Share link revoked', 'Future access will return 422.');
      qc.invalidateQueries({ queryKey: ['compliance-pack', 'shares'] });
    },
    onError: (err) => toast.error('Revoke failed', extractApiError(err).message),
  });

  function copyUrl(url: string) {
    void navigator.clipboard.writeText(url);
    toast.success('Copied', 'Share link on your clipboard.');
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl">Auditor share links</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Token-gated download URLs for an exported Compliance Pack. Hand to your regulator,
            external auditor, or QA partner — no Praxara account required. Time-bounded,
            access-capped, and every fetch is recorded on the audit chain.
          </p>
        </div>
        <Button onClick={() => { setCreateOpen(true); setCreatedUrl(null); }}>
          <Plus className="mr-1.5 h-4 w-4" /> New share link
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Active links</CardTitle>
          <CardDescription>{list.data?.items.length ?? 0} link{(list.data?.items.length ?? 0) === 1 ? '' : 's'} in your tenant.</CardDescription>
        </CardHeader>
        <CardContent>
          {list.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {list.data?.items.length === 0 && (
            <div className="rounded-md border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              No share links yet. Click <strong>New share link</strong> to generate one.
            </div>
          )}
          <div className="space-y-2">
            {list.data?.items.map((s) => {
              const expired = new Date(s.expiresAt).getTime() < Date.now();
              const exhausted = s.accessCount >= s.maxAccess;
              const dead = !!s.revokedAt || expired || exhausted;
              return (
                <div key={s.id} className={`rounded-lg border p-4 ${dead ? 'opacity-60' : ''}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link2 className="h-4 w-4 text-primary" />
                        <span className="font-medium">{s.label ?? <em className="text-muted-foreground">unlabelled</em>}</span>
                        <Badge variant={s.tier === 'FULL' ? 'default' : 'secondary'}>{s.tier}</Badge>
                        {s.revokedAt && <Badge variant="destructive">Revoked</Badge>}
                        {!s.revokedAt && expired && <Badge variant="warning">Expired</Badge>}
                        {!s.revokedAt && !expired && exhausted && <Badge variant="warning">Cap reached</Badge>}
                      </div>
                      <div className="mt-2 grid gap-x-6 gap-y-1 text-xs text-muted-foreground md:grid-cols-3">
                        <div className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Expires {formatDate(s.expiresAt)}</div>
                        <div className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {s.accessCount} / {s.maxAccess} accesses</div>
                        <div className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {s.lastAccessAt ? `Last: ${formatDate(s.lastAccessAt)}${s.lastAccessIp ? ` from ${s.lastAccessIp}` : ''}` : 'Never accessed'}</div>
                      </div>
                    </div>
                    {!dead && (
                      <Button size="sm" variant="ghost" onClick={() => revoke.mutate(s.id)}>
                        <X className="mr-1 h-3.5 w-3.5" /> Revoke
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) { setCreatedUrl(null); setLabel(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdUrl ? 'Share link created' : 'New share link'}</DialogTitle>
            <DialogDescription>
              {createdUrl
                ? 'Copy this URL once — we will not show it again. Anyone with the link can download the pack ZIP until it expires or is revoked.'
                : 'Generates a token-gated download URL for the current Compliance Pack snapshot. Time-bounded + access-capped + audit-logged on every fetch.'}
            </DialogDescription>
          </DialogHeader>

          {!createdUrl && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="tier">Tier</Label>
                <select id="tier" value={tier} onChange={(e) => setTier(e.target.value as 'STARTER' | 'FULL')} className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm">
                  <option value="STARTER">Starter (4 artefacts)</option>
                  <option value="FULL">Full (9 artefacts incl. differential)</option>
                </select>
              </div>
              <div>
                <Label htmlFor="label">Label (optional)</Label>
                <Input id="label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Q1 2026 EMA inspection" />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="expires">Expires in (days)</Label>
                  <Input id="expires" type="number" min={1} max={180} value={expiresInDays} onChange={(e) => setExpiresInDays(Number(e.target.value) || 30)} />
                </div>
                <div>
                  <Label htmlFor="cap">Max accesses</Label>
                  <Input id="cap" type="number" min={1} max={500} value={maxAccess} onChange={(e) => setMaxAccess(Number(e.target.value) || 50)} />
                </div>
              </div>
              <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                The link generates a fresh pack on every access — your tenant data at the moment the auditor downloads, not at link-creation time. Includes differential vs the previous export.
              </div>
            </div>
          )}

          {createdUrl && (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/30 p-3 font-mono text-xs">{createdUrl}</div>
              <Button onClick={() => copyUrl(createdUrl)} className="w-full">
                <Copy className="mr-1.5 h-4 w-4" /> Copy URL to clipboard
              </Button>
              <p className="text-xs text-muted-foreground">
                Send this to the auditor over email or your secure file-sharing channel. They do not need a Praxara account.
              </p>
            </div>
          )}

          <DialogFooter>
            {!createdUrl ? (
              <>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={() => create.mutate()} disabled={create.isPending}>
                  {create.isPending ? 'Generating…' : 'Generate link'}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setCreateOpen(false); setCreatedUrl(null); }}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
